import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import fileDownload from "js-file-download";

//components
import EntryLevelApproval from "./EntryLevelApproval";
import LoadingModal from "../CustomUIComponents/LoadingModal/LoadingModal";
import UserNotification from "../CustomUIComponents/UserNotification/UserNotification";
import withHOC from "../../HOC/HOC";

//actions
import {
  getEntryLevelRequestsAction,
  decideEntryLevelRequestAction,
} from "./EntryLevelApprovalAction";
import { getUserByBranch } from "../UserList/UserListAction";
import { dowloadedAction } from "../../constants/Action";

//constants
import { OPERATION_STATUS } from "../../config/axios.init";
import { USER_ROLES } from "../../constants/appConstants";
import { SELLER_FILTER_ALL } from "./Constants";

//Utility
import Utility from "../../utils/Utility";

//Strings
import { Strings } from "../../resources/i18n/i18n";

// MC-5742: Sales Manager / General Manager worklist to approve/reject customer
// entry level requests
class EntryLevelApprovalContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showLoadingModal: false,
      requests: [],
      numberOfPages: 0,
      expanded: [],
      errorMessage: "",
      toShowToast: false,
      toastMessage: "",
      toastLevel: "",
      sellerOptions: [
        { value: SELLER_FILTER_ALL, label: Strings("seller") },
      ],
    };
    // MC-5742: last filter + page the list was loaded with, so a decision can
    // refresh the same slice the approver is looking at.
    this.searchFilter = {};
  }

  componentDidMount() {
    // The list itself is fetched by EntryLevelApproval once it has built its
    // default filter and pagination request.
    this.getSellerList();
  }

  // MC-5742: full seller (SALES_PERSON) list for the filter dropdown, independent
  // of whatever requests happen to be loaded
  getSellerList = () => {
    getUserByBranch(
      { role: USER_ROLES.salesPerson, branchId: "" },
      (response) => {
        if (response.status === OPERATION_STATUS.SUCCESS) {
          let knownIds = [];
          let sellerOptions = [
            { value: SELLER_FILTER_ALL, label: Strings("seller") },
          ];
          response.data.data.forEach((branch) => {
            branch.us.forEach((user) => {
              if (!knownIds.includes(user.userId)) {
                knownIds.push(user.userId);
                sellerOptions.push({
                  value: user.userId,
                  label: user.name,
                });
              }
            });
          });
          this.setState({ sellerOptions });
        }
      }
    );
  };

  fetchEntryLevelRequests = (searchFilterObject = this.searchFilter) => {
    this.searchFilter = { ...searchFilterObject };
    this.setState({ showLoadingModal: true });
    getEntryLevelRequestsAction(searchFilterObject, (response) => {
      if (response.status === OPERATION_STATUS.SUCCESS) {
        // The paginated response is { mns, nop }; tolerate a bare list so the
        // tab keeps working against a build that is not paginated yet.
        const data = response.data.data || {};
        this.setState({
          requests: (Array.isArray(data) ? data : data.mns) || [],
          numberOfPages: (Array.isArray(data) ? 1 : data.nop) || 0,
          expanded: [],
          showLoadingModal: false,
          errorMessage: "",
        });
      } else {
        this.setState({
          requests: [],
          numberOfPages: 0,
          showLoadingModal: false,
          errorMessage: response.error.message,
        });
      }
    });
  };

  /**
   * MC-5742: records the approver's decision together with the reason they
   * gave for it. The note is mandatory either way - it is the only explanation the
   * salesperson gets - so the backend rejects a decision without one.
   * @param {number} minimartId customer the request belongs to
   * @param {string} status APPROVED | REJECTED
   * @param {string} note the approver's reason
   * @param {function} onSuccess closes the note modal once the decision is taken
   */
  handleDecision = (minimartId, status, note, onSuccess) => {
    // Clear the previous error so repeating a failed decision shows the message
    // again rather than looking like nothing happened.
    this.setState({ showLoadingModal: true, errorMessage: "" });
    decideEntryLevelRequestAction(
      { mid: minimartId, st: status, nt: note },
      (response) => {
        if (response.status === OPERATION_STATUS.SUCCESS) {
          if (onSuccess) {
            onSuccess();
          }
          this.fetchEntryLevelRequests();
        } else {
          this.setState({
            showLoadingModal: false,
            errorMessage: response.error.message,
          });
        }
      }
    );
  };

  /**
   * MC-5742: downloads the entry level requests report for the filters currently
   * applied on the worklist.
   * @param {string} url download url with fileType and the filters as query params
   * @param {string} fileType PDF | CSV
   * @param {boolean} isDownloadFile false when the range exceeds 7 days and the
   *   backend emails the report instead of streaming it
   */
  downloadReports = (url, fileType, isDownloadFile) => {
    this.setState({
      showLoadingModal: true,
      errorMessage: "",
      toShowToast: false,
      toastMessage: "",
      toastLevel: "",
    });
    dowloadedAction(
      url,
      {},
      (response) => {
        if (response.status === OPERATION_STATUS.SUCCESS) {
          if (!isDownloadFile) {
            // Range > 7 days: the backend emailed the report.
            this.setState({
              showLoadingModal: false,
              toShowToast: true,
              toastMessage: "pdfDownload",
              toastLevel: "success",
            });
          } else if (Utility.checkBlobTypeForFileDownload(response.data.type)) {
            fileDownload(
              response.data,
              Strings("ENTRY_LEVEL_REQUESTS_REPORT") +
                "-" +
                Utility.getReportFormattedDateString(
                  Utility.convertDateIntoMiliSeconds(new Date())
                ) +
                Utility.getFileType(fileType)
            );
            this.setState({ showLoadingModal: false });
          } else {
            this.setState({
              showLoadingModal: false,
              toShowToast: true,
              toastMessage: "NoRecordsFound",
              toastLevel: "error",
            });
          }
        } else {
          this.setState({
            showLoadingModal: false,
            errorMessage: response.error.message,
          });
        }
      },
      "EntryLevelRequestsReport",
      isDownloadFile
    );
  };

  handleExpandClick = (id) => {
    let expandedIds = this.state.expanded;
    const index = expandedIds.indexOf(id);
    expandedIds.includes(id)
      ? expandedIds.splice(index, 1)
      : expandedIds.push(id);
    this.setState({ expanded: expandedIds });
  };

  render() {
    return (
      <div>
        {this.state.showLoadingModal && (
          <LoadingModal showLoadingModal={this.state.showLoadingModal} />
        )}
        {this.state.toShowToast && (
          <UserNotification
            key={this.state.toastMessage}
            userNotificationObj={{
              message: Strings(this.state.toastMessage),
              level: this.state.toastLevel,
            }}
          />
        )}
        <EntryLevelApproval
          requests={this.state.requests}
          numberOfPages={this.state.numberOfPages}
          errorMessage={this.state.errorMessage}
          expanded={this.state.expanded}
          sellerOptions={this.state.sellerOptions}
          handleExpandClick={this.handleExpandClick}
          handleDecision={this.handleDecision}
          handleOnClickSearchFilter={this.fetchEntryLevelRequests}
          downloadReports={this.downloadReports}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

const EntryLevelApprovalWrapper = withHOC(EntryLevelApprovalContainer);

export default connect(
  null,
  mapDispatchToProps
)(EntryLevelApprovalWrapper);
